import { Injectable, inject, signal, effect, computed, NgZone, OnDestroy } from '@angular/core';
import {
  Firestore,
  collection,
  addDoc,
  doc,
  updateDoc,
  query,
  where,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
  getDocs,
  limit,
} from '@angular/fire/firestore';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppNotification } from '../models/notification.model';
import { AuthService } from './auth.service';
import { formatTime12 } from '../utils/time.util';

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private readonly firestore = inject(Firestore);
  private readonly auth = inject(AuthService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly zone = inject(NgZone);
  private readonly col = collection(this.firestore, 'notifications');
  private unsubscribe: Unsubscribe | null = null;
  private listenUid: string | null = null;
  private seenIds = new Set<string>();
  private primed = false;

  private readonly itemsSignal = signal<AppNotification[]>([]);
  readonly notifications = this.itemsSignal.asReadonly();
  readonly unreadCount = signal(0);

  private readonly ownerContext = computed(() => ({
    uid: this.auth.firebaseUser()?.uid ?? this.auth.profile()?.uid ?? null,
    isOwner: this.auth.isOwner(),
  }));

  constructor() {
    effect(() => {
      const { uid, isOwner } = this.ownerContext();
      this.startListening(isOwner ? uid : null);
    });

    effect(() => {
      const items = this.notifications();
      this.unreadCount.set(items.filter((n) => !n.read).length);

      if (!this.primed) {
        items.forEach((n) => this.seenIds.add(n.id));
        this.primed = true;
        return;
      }

      for (const n of items) {
        if (!this.seenIds.has(n.id) && !n.read) {
          this.seenIds.add(n.id);
          this.snackBar.open(n.message, 'View', { duration: 6000 });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe?.();
  }

  private startListening(uid: string | null): void {
    if (uid === this.listenUid) return;
    this.listenUid = uid;

    this.unsubscribe?.();
    this.unsubscribe = null;
    this.primed = false;
    this.seenIds.clear();

    if (!uid) {
      this.itemsSignal.set([]);
      return;
    }

    const q = query(this.col, where('ownerId', '==', uid), limit(30));

    this.unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const rows = snapshot.docs
          .map((d) => this.mapNotification({ id: d.id, ...d.data() }))
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        this.zone.run(() => this.itemsSignal.set(rows));
      },
      (err) => {
        console.error('notifications onSnapshot failed', err);
        this.zone.run(() => this.itemsSignal.set([]));
      },
    );

    void getDocs(q)
      .then((snapshot) => {
        const rows = snapshot.docs
          .map((d) => this.mapNotification({ id: d.id, ...d.data() }))
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        this.zone.run(() => {
          if (this.itemsSignal().length === 0) {
            this.itemsSignal.set(rows);
          }
        });
      })
      .catch((err) => console.error('notifications getDocs failed', err));
  }

  async createNewBookingAlert(input: {
    ownerId: string;
    bookingId: string;
    serviceName: string;
    customerName: string;
    date: string;
    startTime: string;
  }): Promise<void> {
    await addDoc(this.col, {
      ownerId: input.ownerId,
      type: 'new_booking',
      bookingId: input.bookingId,
      title: 'New booking',
      message: `${input.customerName} booked ${input.serviceName} on ${input.date} at ${formatTime12(input.startTime)}.`,
      read: false,
      createdAt: serverTimestamp(),
    });
  }

  async markRead(id: string): Promise<void> {
    await updateDoc(doc(this.firestore, `notifications/${id}`), { read: true });
  }

  async markAllRead(ids: string[]): Promise<void> {
    await Promise.all(ids.map((id) => this.markRead(id)));
  }

  private mapNotification(raw: Record<string, unknown>): AppNotification {
    const createdAt = raw['createdAt'] as { toDate?: () => Date } | Date | undefined;
    return {
      id: String(raw['id'] ?? ''),
      ownerId: String(raw['ownerId'] ?? ''),
      type: 'new_booking',
      bookingId: String(raw['bookingId'] ?? ''),
      title: String(raw['title'] ?? ''),
      message: String(raw['message'] ?? ''),
      read: Boolean(raw['read']),
      createdAt:
        createdAt && typeof createdAt === 'object' && 'toDate' in createdAt && createdAt.toDate
          ? createdAt.toDate()
          : createdAt instanceof Date
            ? createdAt
            : new Date(),
    };
  }
}
